import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Tooltip from "@mui/material/Tooltip";
import { useContext, useEffect, useRef, useState } from "react";
import { InfoCircle } from "react-bootstrap-icons";
import "../../../../Tooltip/styles/tooltip.css";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import { FormContext } from "../context/FormProvider.jsx";
import { handleKeyDown, handleShowTitlePlaceholder } from "../main-helper.js";
import * as Messages from "../messages/messages.js";
import "../styles/form-container.css";
import "./styles/text-field.css";

// NewTitle component for text input fields used for individuals titles
const NewTitle = ({
  idProp,
  label,
  val,
  state,
  setState,
  req,
  debouncedSave,
  setIsLoading,
  autoData,
  disabledInput,
  invalidData,
}) => {
  const textRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [openHelper, setOpenHelper] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [placeholder, setPlaceholder] = useState("");
  const [suggestedTitle, setSuggestedTitle] = useState("");
  const [adjustMargin, setAdjustMargin] = useState(false);
  const { requestStatus, handleInputFocus, handleInputDisable } =
    useContext(FormContext);
  const { clientId, projectId, queryClient, userId } =
    useContext(DashboardContext);

  // Get the current project data
  const formData = queryClient.getQueryData([
    "currentProject",
    clientId,
    projectId,
  ]);

  // Attempts to find a title used before for a specific individual
  useEffect(() => {
    handleShowTitlePlaceholder(
      idProp,
      state,
      formData,
      placeholder,
      setPlaceholder,
      setSuggestedTitle,
      showPlaceholder,
    );
  }, [showPlaceholder, state, idProp, formData]);

  useEffect(() => {
    if (req && openHelper) {
      setAdjustMargin(true);
    } else {
      setAdjustMargin(false);
    }
  }, [req, openHelper]);

  const handleTitleTextChange = (e) => {
    let newState;

    let value = e.target.value.replace(/[^a-zA-Z0-9- ,'.& #]/g, "");
    newState = { ...state, [idProp]: value };
    setState(newState);

    if (
      placeholder === "" ||
      !placeholder.toLowerCase().startsWith(value.toLowerCase())
    ) {
      if (autoData && autoData.length > 0) {
        let match = autoData.find((title) => {
          if (!title) return false;
          return title.toLowerCase().startsWith(value.toLowerCase());
        });

        if (match) {
          match = value + match.slice(value.length);
        }
        if (match && value !== "") {
          setPlaceholder(match);
          setShowPlaceholder(true);
        } else if (value === "" && suggestedTitle !== "") {
          setPlaceholder(suggestedTitle);
          setShowPlaceholder(true);
        } else {
          setPlaceholder("");
          setShowPlaceholder(false);
        }
      }
    } else if (
      suggestedTitle !== "" &&
      suggestedTitle.toLowerCase().startsWith(value.toLowerCase())
    ) {
      let existingMatch = value + suggestedTitle.slice(value.length);
      setPlaceholder(existingMatch);
      setShowPlaceholder(true);
    }

    if (debouncedSave && setIsLoading && requestStatus !== "A") {
      setIsLoading(true);
      debouncedSave.mutate(newState);
    }
  };

  return (
    <div className="text-info-container title-info-container">
      <FormControl
        variant="outlined"
        id="text-form"
        classes={{ root: "form-text-root" }}
        className={`title-form-control ${
          requestStatus === "RV" &&
          disabledInput === false &&
          invalidData &&
          (val === invalidData["initialValue"] || val === "")
            ? "revise-input"
            : requestStatus === "RV" &&
                disabledInput === false &&
                invalidData &&
                val !== invalidData["initialValue"] &&
                val !== ""
              ? "revised-input"
              : req
                ? "required"
                : ""
        }`}
      >
        <InputLabel
          id="text-field-label"
          htmlFor="text-input"
          classes={{ shrink: "text-field-label-shrink" }}
          className={`${idProp}-text-field-label`}
        >
          {`${label}`}
        </InputLabel>
        <OutlinedInput
          type="text"
          id="text-field"
          className={`${idProp}-input title-input`}
          ref={textRef}
          label={`${label}`}
          value={val}
          onChange={handleTitleTextChange}
          onFocus={() => handleInputFocus(textRef, setOpenHelper)}
          onSelect={() => {
            if (state[`${idProp}`] === "") {
              setOpen(true);
            } else {
              setOpen(false);
            }
            setOpenHelper(true);
            setShowPlaceholder(true);
          }}
          onBlur={() => {
            setOpen(false);
            setShowPlaceholder(false);
            setOpenHelper(false);
            setPlaceholder("");
          }}
          onKeyDown={(e) =>
            handleKeyDown(
              e,
              idProp,
              state,
              setState,
              autoData,
              placeholder,
              debouncedSave,
              setIsLoading,
              setPlaceholder,
              setShowPlaceholder,
              setOpen,
              setOpenHelper,
            )
          }
          disabled={handleInputDisable(idProp, state, disabledInput, userId)}
          autoComplete="off"
          autoCapitalize="words"
          classes={{
            root: "text-field-root",
            notchedOutline: "text-notchedOutline",
            input: "text-field-input",
          }}
          endAdornment={
            <Tooltip
              id="clear-btn-tooltip"
              classes={{
                tooltip: "tooltip-clear-btn",
                popper: "popper-clear-btn",
                tooltipPlacementTop: "tooltip-top",
              }}
              title={val === "" ? "" : "Clear Field"}
              placement="top"
            >
              <InputAdornment
                className="text-adornment"
                id="text-adornment"
                position="end"
                style={
                  val === "" ||
                  handleInputDisable(idProp, state, disabledInput, userId) ===
                    true
                    ? { visibility: "hidden" }
                    : { visibility: "visible" }
                }
              >
                <IconButton
                  style={
                    val === ""
                      ? {
                          pointerEvents: "none",
                          cursor: "default",
                        }
                      : {
                          pointerEvents: "auto",
                          cursor: "pointer",
                        }
                  }
                  aria-label="clear input field"
                  onClick={() => {
                    if (debouncedSave) {
                      let newState = {
                        ...state,
                        [idProp]: "",
                      };
                      setState(newState);
                      setIsLoading(true);
                      setPlaceholder("");
                      setShowPlaceholder(false);
                      debouncedSave.mutate(newState);
                    } else {
                      let newState = {
                        ...state,
                        [idProp]: "",
                      };
                      setState(newState);
                      setPlaceholder("");
                      setShowPlaceholder(false);
                    }
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  className="clear-icon-button"
                  id="clear-icon-button"
                  disabled={val === "" ? true : false}
                >
                  <CloseIcon id="clear-input-icon" className="close-icon" />
                </IconButton>
              </InputAdornment>
            </Tooltip>
          }
        />
      </FormControl>
      <input
        key={`${idProp}-placeholder`}
        className={`${
          req ? "predictive-placeholder shift" : "predictive-placeholder"
        }`}
        type="text"
        autoComplete="off"
        id="title-placeholder"
        value={placeholder}
        disabled={true}
      />
      <Tooltip
        id="tf-tooltip"
        classes={{
          tooltip: "text-field-tooltip",
          popper: "text-field-popper",
          arrow: "text-field-arrow",
          tooltipPlacementLeft: "text-field-right",
        }}
        title={Messages[`${idProp}Message`]}
        placement={"top"}
        arrow
        open={open}
        sx={{
          backgroundColor: "rgb(0, 0, 0, 0.8) !important",
        }}
      >
        <InfoCircle
          className="text-info-circ"
          id={`info-${idProp}`}
          color="white"
          size={30}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        />
      </Tooltip>
    </div>
  );
};
export default NewTitle;
